import type { Translations } from "../../i18n/en";
import {
    ALLOWED_SYNC_FREQUENCY_MINUTES,
    DEFAULT_SYNC_FREQUENCY_MINUTES,
    type SyncFrequencyMinutes,
} from "../../types/config";

/**
 * How a sync cadence is said, in one table.
 *
 * The cadence is stored in minutes and the screens phrase it in hours, and
 * before this there were four places that did the conversion themselves — the
 * admin picker, the admin table cell, the sidebar's popover and the Booking
 * Pace Monitor's description — each a binary `minutes === 30 ? … : …` over a
 * two-value set. At six cadences that is four conditionals to widen and four
 * chances for one to be forgotten, which is the "one table" rule the pricing
 * section icons already follow.
 *
 * **The phrase is a whole string per cadence, not a number in a frame.**
 * Czech declines the noun with the count and the quantifier with it — "každou
 * hodinu", "každé 2 hodiny", "každých 6 hodin" — so a `"každých {count}
 * hodin"` template is wrong for two of the six values. English gets the same
 * treatment for free ("every hour", not "every 1 hours"). Adding a cadence
 * means adding its key to `en.ts`, which the `Translations` type then demands
 * of `cs.ts`.
 *
 * Two frames, differing only in the initial capital, because both are real:
 * {@link syncCadencePhrase} reads mid-sentence ("checks for new reservations
 * every 6 hours"), {@link syncCadenceLabel} stands alone in a picker or a
 * table cell.
 */

/**
 * Read a stored cadence back as one the product can name.
 *
 * The admin list types the column as a plain `number` and a row could still
 * carry a cadence a future release retires, so this is the same
 * normalize-on-read the server's `normalizeSyncFrequencyMinutes` does — a
 * cadence nobody can name reads as the default rather than rendering blank.
 */
export function normalizeSyncCadence(value: number | null | undefined): SyncFrequencyMinutes {
    return (ALLOWED_SYNC_FREQUENCY_MINUTES as readonly number[]).includes(value ?? NaN)
        ? (value as SyncFrequencyMinutes)
        : DEFAULT_SYNC_FREQUENCY_MINUTES;
}

/** "every 6 hours" — for use inside a sentence. */
export function syncCadencePhrase(minutes: number | null | undefined, t: Translations): string {
    return t.syncCadence[normalizeSyncCadence(minutes)];
}

/** "Every 6 hours" — for a picker option, a table cell or a status detail. */
export function syncCadenceLabel(minutes: number | null | undefined, t: Translations): string {
    const phrase = syncCadencePhrase(minutes, t);
    // Safe to capitalise blindly: every phrase in the table opens with the
    // locale's own word for "every", never a proper noun.
    return phrase.charAt(0).toUpperCase() + phrase.slice(1);
}
